import { ApolloLink } from '@apollo/client'
import type { OperationDefinitionNode } from 'graphql'
import { Kind } from 'graphql'

import { setAttribute } from './newRelicAdapter'

/**
 * NR custom attribute keys set by this link — exported so NRQL queries and
 * dashboards can reference them without duplicating the string literals.
 *
 * Note: New Relic custom attributes are session-global and last-write-wins.
 * Concurrent operations will overwrite each other's values; these attributes
 * reflect the most recent operation at any sampling point, not per-request
 * isolation.
 */
export const NR_ATTR = {
  APP: 'consumer.app',
  OPERATION: 'consumer.operation',
  OPERATION_TYPE: 'consumer.operationType',
  TRACKING_ID: 'consumer.trackingId',
} as const

/**
 * Creates an Apollo link that sets New Relic custom attributes before each
 * GraphQL operation. Both CMA (via newRelicAdapter.native.ts) and CWA (via
 * newRelicAdapter.web.ts) pick up the correct platform SDK through Metro and
 * Vite platform-extension resolution respectively.
 *
 * Attributes set per operation:
 *  - consumer.app         — 'mobile' | 'web'
 *  - consumer.operation   — operation name, or 'unknown' if unnamed
 *  - consumer.operationType — 'query' | 'mutation' | 'subscription'
 *  - consumer.trackingId  — variables.trackingId when present, '' otherwise
 *                           (always written so a stale id from a prior operation
 *                           does not persist on subsequent operations without one)
 */
export function createNewRelicAttributeLink(platform: 'mobile' | 'web'): ApolloLink {
  return new ApolloLink((operation, forward) => {
    setAttribute(NR_ATTR.APP, platform)
    setAttribute(NR_ATTR.OPERATION, operation.operationName ?? 'unknown')

    const def = operation.query.definitions.find(
      (d): d is OperationDefinitionNode => d.kind === Kind.OPERATION_DEFINITION
    )
    if (def) {
      setAttribute(NR_ATTR.OPERATION_TYPE, def.operation)
    }

    const trackingId = operation.variables?.trackingId
    setAttribute(NR_ATTR.TRACKING_ID, trackingId != null ? String(trackingId) : '')

    return forward ? forward(operation) : null
  })
}
